import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, History, Loader2, Plus, Send, Sparkles, Square, ThumbsDown, ThumbsUp, Undo2, X } from "lucide-react";
import { aiApi, useJobStream, type AIChangeset, type AIConflict, type AIEstimate, type AIJob, type JobEvent } from "../lib/ai";
import { ApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { Key } from "../locales/en";
import { Banner, Button, cx, Modal, Select, Textarea, useToast } from "./ui";

// The AI Builder panel (brief section 8): a conversation with the AI, its
// live steps, and the changes it prepared to preview, apply, discard or
// undo. Docked beside the theme editor on desktop, a bottom sheet on phones;
// also opened from the product and collection lists for bulk content.

type Props = {
  storeId: string;
  themeId?: string | null; // fixed when opened from the theme editor
  productIds?: string[];   // picked on the product list
  onClose: () => void;
  onChanged?: () => void;  // the draft changed: refresh the preview
  className?: string;
};

const finished = (s: string) => !["running", "queued", "waiting", "batch"].includes(s);

export function AIPanel({ storeId, themeId, productIds, onClose, onChanged, className }: Props) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const ai = useMemo(() => aiApi(storeId), [storeId]);
  const status = useQuery({ queryKey: ["ai", storeId, "status"], queryFn: ai.status, staleTime: 30_000 });
  const suggestions = useQuery({ queryKey: ["ai", storeId, "suggestions"], queryFn: ai.suggestions, staleTime: 60_000, enabled: !!status.data?.enabled });
  const sessions = useQuery({ queryKey: ["ai", storeId, "sessions"], queryFn: ai.sessions, enabled: !!status.data?.enabled });

  const [theme, setTheme] = useState<string | null>(themeId ?? null);
  useEffect(() => {
    if (themeId !== undefined) setTheme(themeId);
    else if (!theme && status.data?.themes.length) setTheme(status.data.themes.find((x) => x.role === "main")?.id ?? null);
  }, [themeId, status.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [sessionId, setSessionId] = useState<string | null>(null);
  const conv = useQuery({ queryKey: ["ai", storeId, "conversation", sessionId], queryFn: () => ai.conversation(sessionId!), enabled: !!sessionId });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [estimate, setEstimate] = useState<{ est: AIEstimate; text: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // The running job's live state.
  const [jobId, setJobId] = useState<string | null>(null);
  const [live, setLive] = useState<{ text: string; steps: string[]; thinking: boolean; status: string; question?: string; items?: number }>({ text: "", steps: [], thinking: false, status: "" });
  const [stopping, setStopping] = useState(false);
  useJobStream(storeId, jobId, (e: JobEvent) => {
    switch (e.type) {
      case "text":
        setLive((l) => ({ ...l, text: l.text + e.data.delta, thinking: false }));
        break;
      case "thinking":
        setLive((l) => ({ ...l, thinking: true }));
        break;
      case "step": {
        const key = e.data.tool ? `ai.step.${e.data.tool}` : `ai.step.${e.data.step}`;
        setLive((l) => (l.steps.at(-1) === key ? l : { ...l, steps: [...l.steps, key] }));
        break;
      }
      case "status":
        setLive((l) => ({ ...l, status: e.data.status, items: e.data.items ?? l.items }));
        break;
      case "changeset":
        onChanged?.();
        break;
      case "question":
        setLive((l) => ({ ...l, question: e.data.question }));
        break;
      case "done":
        setLive((l) => ({ ...l, status: e.data.status }));
        setStopping(false);
        onChanged?.();
        void qc.invalidateQueries({ queryKey: ["ai", storeId] });
        if (e.data.status !== "batch") setJobId(null);
        break;
    }
  });
  // Pick up a job that's still going when the panel opens on a conversation.
  useEffect(() => {
    const running = conv.data?.jobs.find((j) => !finished(j.status) && j.status !== "waiting");
    if (running && running.id !== jobId) {
      setLive({ text: "", steps: [], thinking: false, status: running.status, items: running.items });
      setJobId(running.id);
    }
  }, [conv.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight }); }, [conv.data, live.text, live.steps.length]);

  const send = async (msg: string, confirm = false) => {
    const body = msg.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      let sid = sessionId;
      if (!sid) {
        sid = (await ai.newSession(theme)).id;
        setSessionId(sid);
      }
      const res = await ai.send(sid, { text: body, product_ids: productIds?.length ? productIds : undefined, confirm });
      if (res.estimate) {
        setEstimate({ est: res.estimate, text: body });
        return;
      }
      setText("");
      setLive({ text: "", steps: [], thinking: false, status: "running" });
      setJobId(res.job!.id);
      await qc.invalidateQueries({ queryKey: ["ai", storeId, "conversation", sid] });
    } catch (e) {
      toast((e as Error).message, true);
    } finally {
      setSending(false);
    }
  };

  const stop = async () => {
    if (!jobId) return;
    setStopping(true);
    try {
      await ai.stop(jobId);
    } catch (e) {
      setStopping(false);
      toast((e as Error).message, true);
    }
  };

  const credits = status.data?.credits;
  const busy = !!jobId && !finished(live.status) && live.status !== "batch";
  const jobs = conv.data?.jobs ?? [];
  const messages = conv.data?.messages ?? [];

  return (
    <aside aria-label={t("ai.title")} className={cx("flex min-h-0 flex-col bg-white", className)}>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        <Sparkles className="size-4 text-brand" aria-hidden />
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{t("ai.title")}</h2>
        {credits && <span className="text-xs text-ink-muted" title={t("ai.credits_today", { n: Math.floor(credits.left_today) })}>{t("ai.credits_left", { n: Math.floor(credits.left_month) })}</span>}
        <button type="button" onClick={() => setHistoryOpen((v) => !v)} className="rounded p-1.5 text-ink-muted hover:bg-subtle hover:text-ink" aria-label={t("ai.history")} title={t("ai.history")}><History className="size-4" /></button>
        <button type="button" onClick={() => { setSessionId(null); setJobId(null); setLive({ text: "", steps: [], thinking: false, status: "" }); }} className="rounded p-1.5 text-ink-muted hover:bg-subtle hover:text-ink" aria-label={t("ai.new_chat")} title={t("ai.new_chat")}><Plus className="size-4" /></button>
        <button type="button" onClick={onClose} className="rounded p-1.5 text-ink-muted hover:bg-subtle hover:text-ink" aria-label={t("ai.close")}><X className="size-4" /></button>
      </header>

      {historyOpen && (
        <div className="max-h-56 shrink-0 overflow-y-auto border-b border-line p-2">
          {(sessions.data ?? []).map((s) => (
            <button key={s.id} type="button" onClick={() => { setSessionId(s.id); setHistoryOpen(false); setJobId(null); }}
              className={cx("block w-full truncate rounded-md px-2 py-1.5 text-start text-[13px] hover:bg-subtle", s.id === sessionId && "bg-brand-tint text-brand")}>{s.title || "…"}</button>
          ))}
        </div>
      )}

      <div ref={scroller} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {status.data && !status.data.enabled && <Banner tone="warning">{status.data.reason === "no_key" ? t("ai.off_no_key") : t("ai.off")}</Banner>}
        {credits && credits.left_month <= 0 && <Banner tone="warning">{t("ai.out_of_credits", { date: new Date(credits.renews_at).toLocaleDateString() })}</Banner>}

        {!sessionId && status.data?.enabled && (
          <div className="space-y-3">
            {themeId === undefined && (status.data.themes.length > 0) && (
              <label className="block text-[13px]">
                <span className="mb-1 block font-medium">{t("ai.pick_theme")}</span>
                <Select value={theme ?? ""} onChange={(e) => setTheme(e.target.value || null)}>
                  <option value="">{t("ai.no_theme")}</option>
                  {status.data.themes.map((x) => <option key={x.id} value={x.id}>{x.role === "main" ? t("ai.theme_live", { name: x.name }) : x.name}</option>)}
                </Select>
              </label>
            )}
            <p className="text-[13px] text-ink-muted">{t("ai.empty_intro")}</p>
            {!!productIds?.length && <p className="text-[13px] font-medium">{t("ai.selected", { count: productIds.length })}</p>}
            <div className="flex flex-wrap gap-2">
              {(suggestions.data ?? []).map((s) => {
                const label = t(`ai.suggest.${s.kind}` as Key, { count: s.count });
                return <button key={s.kind} type="button" onClick={() => void send(label)} className="rounded-full border border-line px-3 py-1 text-start text-[12px] hover:border-brand hover:text-brand">{label}</button>;
              })}
              {theme && ["theme_darker", "theme_offers"].map((k) => {
                const label = t(`ai.suggest.${k}` as Key);
                return <button key={k} type="button" onClick={() => void send(label)} className="rounded-full border border-line px-3 py-1 text-start text-[12px] hover:border-brand hover:text-brand">{label}</button>;
              })}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={cx("flex", m.role === "user" ? "justify-end" : "justify-start")}>
            <p className={cx("max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[13px]", m.role === "user" ? "bg-brand text-white" : "bg-subtle text-ink")}>{m.text}</p>
          </div>
        ))}

        {jobs.filter((j) => finished(j.status) || j.status === "waiting" || j.status === "batch").map((j) => (
          <JobResult key={j.id + j.status} storeId={storeId} job={j} onChanged={onChanged} />
        ))}

        {jobId && (busy || live.status === "batch") && (
          <div className="space-y-2 rounded-xl border border-line p-3" aria-live="polite">
            {live.status === "batch" ? (
              <p className="text-[13px]">{t("ai.status.batch", { count: live.items ?? 0 })}</p>
            ) : (
              <>
                <ul className="space-y-1">
                  {live.steps.map((s, i) => (
                    <li key={i} className="flex items-center gap-2 text-[12px] text-ink-muted">
                      {i === live.steps.length - 1 ? <Loader2 className="size-3.5 animate-spin text-brand" /> : <Check className="size-3.5 text-success" />}
                      {t(s as Key)}
                    </li>
                  ))}
                  {live.steps.length === 0 && <li className="flex items-center gap-2 text-[12px] text-ink-muted"><Loader2 className="size-3.5 animate-spin text-brand" />{live.status === "queued" ? t("ai.status.queued") : live.thinking ? t("ai.thinking") : t("ai.working")}</li>}
                </ul>
                {live.text && <p className="whitespace-pre-wrap text-[13px]">{live.text}</p>}
                <Button size="sm" onClick={() => void stop()} loading={stopping}><Square className="size-3.5" />{stopping ? t("ai.stopping") : t("ai.stop")}</Button>
              </>
            )}
          </div>
        )}
      </div>

      <form className="shrink-0 border-t border-line p-3" onSubmit={(e) => { e.preventDefault(); void send(text); }}>
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("ai.placeholder")} disabled={!status.data?.enabled || busy}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(text); } }} aria-label={t("ai.placeholder")} />
        <div className="mt-2 flex items-center gap-2">
          <p className="min-w-0 flex-1 text-[11px] text-ink-subtle">{t("ai.disclaimer")}</p>
          <Button type="submit" variant="primary" size="sm" loading={sending} disabled={!text.trim() || !status.data?.enabled || busy}><Send className="size-3.5 rtl:-scale-x-100" />{t("ai.send")}</Button>
        </div>
      </form>

      {estimate && (
        <Modal open title={t("ai.estimate_title")} onClose={() => setEstimate(null)}
          footer={<>
            <Button onClick={() => setEstimate(null)}>{t("ai.cancel")}</Button>
            <Button variant="primary" onClick={() => { const m = estimate.text; setEstimate(null); void send(m, true); }}>{t("ai.estimate_confirm")}</Button>
          </>}>
          <p className="text-sm">{t("ai.estimate_body", { credits: Math.ceil(estimate.est.credits), left: Math.floor(credits?.left_month ?? 0) })}</p>
          {estimate.est.background && <p className="mt-2 text-sm text-ink-muted">{t("ai.estimate_background")}</p>}
        </Modal>
      )}
    </aside>
  );
}

// JobResult is a finished job: its outcome, its changes and feedback.
function JobResult({ storeId, job, onChanged }: { storeId: string; job: AIJob; onChanged?: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const ai = useMemo(() => aiApi(storeId), [storeId]);
  const cs = useQuery({ queryKey: ["ai", storeId, "changeset", job.changeset_id], queryFn: () => ai.changeset(job.changeset_id!), enabled: !!job.changeset_id });
  const [details, setDetails] = useState(false);
  const [working, setWorking] = useState(false);
  const [conflicts, setConflicts] = useState<AIConflict[] | null>(null);
  const [resolve, setResolve] = useState<Record<string, "ai" | "mine">>({});
  const [rated, setRated] = useState<1 | -1 | null>(job.feedback);
  const [comment, setComment] = useState("");

  const run = async (fn: () => Promise<AIChangeset | { changeset: AIChangeset; conflicts?: AIConflict[] }>, done?: string) => {
    setWorking(true);
    try {
      await fn();
      if (done) toast(done);
      onChanged?.();
      await qc.invalidateQueries({ queryKey: ["ai", storeId] });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && Array.isArray(e.body.conflicts)) {
        setConflicts(e.body.conflicts as AIConflict[]);
        setResolve(Object.fromEntries((e.body.conflicts as AIConflict[]).map((c) => [c.item_id, "ai" as const])));
      } else if (e instanceof ApiError && e.code === "ai_changeset_invalid") {
        toast(t("ai.invalid_changes"), true);
      } else {
        toast((e as Error).message, true);
      }
    } finally {
      setWorking(false);
    }
  };

  const c = cs.data;
  const content = c?.items.filter((i) => i.kind === "content") ?? [];
  const problem = job.status === "failed" || job.status === "budget" || job.status === "cancelled";
  return (
    <div className="space-y-2 rounded-xl border border-line p-3">
      {job.status === "waiting" ? (
        <p className="text-[13px] font-medium">{t("ai.status.waiting")}</p>
      ) : problem ? (
        <Banner tone={job.status === "failed" ? "critical" : "warning"}>{job.error || t(`ai.status.${job.status}` as Key)}</Banner>
      ) : job.summary && job.status === "finished" ? (
        <p className="whitespace-pre-wrap text-[13px]">{job.summary}</p>
      ) : null}

      {c && c.items.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="flex-1 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle">{t("ai.changes_title")}</h3>
            {c.status !== "draft" && <span className="text-[12px] text-ink-muted">{t(`ai.${c.status}` as Key)}</span>}
          </div>
          <ul className="list-disc space-y-0.5 ps-5 text-[13px]">{c.summary.map((s, i) => <li key={i}>{s}</li>)}</ul>
          {c.status === "draft" && <p className="text-[11px] text-ink-subtle">{t("ai.preview_note")}</p>}
          <button type="button" onClick={() => setDetails((v) => !v)} className="flex items-center gap-1 text-[12px] font-medium text-brand">
            <ChevronDown className={cx("size-3.5 transition-transform", details && "rotate-180")} />{details ? t("ai.hide_details") : t("ai.show_details")}
          </button>
          {details && (
            <div className="space-y-2">
              {content.length > 0 && (
                <table className="w-full table-fixed text-[12px]">
                  <thead><tr className="text-ink-subtle"><th className="w-8" /><th className="text-start font-medium">{t("ai.before")}</th><th className="text-start font-medium">{t("ai.after")}</th></tr></thead>
                  <tbody>
                    {content.map((it) => (
                      <tr key={it.id} className="align-top">
                        <td className="py-1">
                          <input type="checkbox" checked={it.accepted} disabled={c.status !== "draft" || working} aria-label={`${t("ai.include")}: ${it.label}`}
                            onChange={(e) => void run(() => ai.review(c.id, [it.id], e.target.checked))} />
                        </td>
                        <td className="py-1 pe-2 text-ink-muted"><span className="block font-medium text-ink">{it.label}{it.target?.locale === "ar" ? " · ع" : ""}</span><span className="line-clamp-4 break-words" dir="auto">{stripTags(String(it.before ?? ""))}</span></td>
                        <td className="py-1"><span className="line-clamp-6 break-words" dir="auto">{stripTags(String(it.after ?? ""))}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {c.items.filter((i) => i.kind !== "content").map((it) => (
                <details key={it.id} className="rounded-md border border-line">
                  <summary className="cursor-pointer px-2 py-1 font-mono text-[11px]">{it.kind === "new_file" ? `${t("ai.new_section_file")}: ${it.path}` : it.path}</summary>
                  <pre className="max-h-64 overflow-auto bg-subtle p-2 text-[11px]" dir="ltr">{typeof it.after === "string" ? it.after : JSON.stringify(it.after, null, 2)}</pre>
                </details>
              ))}
            </div>
          )}
          {c.status === "draft" && job.status !== "running" && (
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" size="sm" loading={working} onClick={() => void run(() => ai.apply(c.id), t("ai.applied_toast"))}><Check className="size-3.5" />{t("ai.apply")}</Button>
              <Button size="sm" disabled={working} onClick={() => void run(() => ai.discard(c.id))}>{t("ai.discard")}</Button>
            </div>
          )}
          {c.status === "applied" && (
            <Button size="sm" loading={working} onClick={() => void run(() => ai.undo(c.id), t("ai.undone_toast"))}><Undo2 className="size-3.5" />{t("ai.undo")}</Button>
          )}
        </div>
      )}
      {c && c.items.length === 0 && job.status === "finished" && job.task_type !== "short_answer" && <p className="text-[12px] text-ink-muted">{t("ai.changes_none")}</p>}

      {finishedStatus(job.status) && (
        <div className="flex items-center gap-1 pt-1">
          {rated ? <span className="text-[12px] text-ink-muted">{t("ai.feedback_thanks")}</span> : (
            <>
              <button type="button" className="rounded p-1 text-ink-subtle hover:text-success" aria-label={t("ai.feedback_up")} title={t("ai.feedback_up")}
                onClick={() => { setRated(1); void ai.feedback(job.id, 1).catch(() => setRated(null)); }}><ThumbsUp className="size-3.5" /></button>
              <button type="button" className="rounded p-1 text-ink-subtle hover:text-danger" aria-label={t("ai.feedback_down")} title={t("ai.feedback_down")}
                onClick={() => setRated(-1)}><ThumbsDown className="size-3.5" /></button>
            </>
          )}
          {rated === -1 && job.feedback !== -1 && (
            <form className="flex flex-1 gap-1" onSubmit={(e) => { e.preventDefault(); void ai.feedback(job.id, -1, comment).then(() => toast(t("ai.feedback_thanks"))); }}>
              <input className="min-w-0 flex-1 rounded-md border border-line px-2 text-[12px]" placeholder={t("ai.feedback_comment")} value={comment} onChange={(e) => setComment(e.target.value)} />
              <Button size="sm" type="submit">{t("ai.feedback_send")}</Button>
            </form>
          )}
        </div>
      )}

      {conflicts && c && (
        <Modal open title={t("ai.conflicts_title")} onClose={() => setConflicts(null)}
          footer={<>
            <Button onClick={() => setConflicts(null)}>{t("ai.cancel")}</Button>
            <Button variant="primary" loading={working} onClick={() => { setConflicts(null); void run(() => ai.apply(c.id, resolve), t("ai.applied_toast")); }}>{t("ai.apply_resolved")}</Button>
          </>}>
          <p className="mb-3 text-sm">{t("ai.conflicts_body")}</p>
          <ul className="space-y-3">
            {conflicts.map((cf) => (
              <li key={cf.item_id} className="text-sm">
                <p className="font-medium">{cf.label}</p>
                <div className="mt-1 flex gap-4">
                  {(["ai", "mine"] as const).map((v) => (
                    <label key={v} className="flex items-center gap-1.5">
                      <input type="radio" name={cf.item_id} checked={resolve[cf.item_id] === v} onChange={() => setResolve((r) => ({ ...r, [cf.item_id]: v }))} />
                      {v === "ai" ? t("ai.keep_ai") : t("ai.keep_mine")}
                    </label>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
}

const finishedStatus = (s: string) => s === "finished" || s === "failed" || s === "budget";
const stripTags = (s: string) => s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

// AIDrawer opens the panel over a list page (products, collections):
// from the end side on desktop, as a bottom sheet on phones.
export function AIDrawer({ storeId, productIds, onClose }: { storeId: string; productIds?: string[]; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50">
      <button type="button" aria-hidden tabIndex={-1} className="absolute inset-0 bg-black/30" onClick={onClose} />
      <AIPanel storeId={storeId} productIds={productIds} onClose={onClose}
        className="absolute inset-x-0 bottom-0 h-[80vh] rounded-t-2xl shadow-pop md:inset-y-0 md:end-0 md:start-auto md:h-auto md:w-[420px] md:rounded-none" />
    </div>
  );
}
